import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import AccountantLayout from '../components/AccountantLayout';

const EditClientPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    industry: '',
    address: '',
    vatNumber: '',
    billingContact: '',
    notes: '',
  });

  const industries = [
    'Construction',
    'Transport',
    'Manufacturing',
    'Real Estate',
    'SaaS',
    'Retail',
    'Healthcare',
    'Finance',
    'Other',
  ];

  useEffect(() => {
    loadClient();
  }, [id]);

  const loadClient = async () => {
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setFormData({
        name: 'Atlas Construction Co',
        email: 'contact@atlasconstruction.com',
        phone: '+32 2 123 4567',
        industry: 'Construction',
        address: 'Avenue Louise 123, 1050 Brussels, Belgium',
        vatNumber: 'BE0123456789',
        billingContact: 'John Smith',
        notes: 'Premium client with monthly billing cycle',
      });
      setLoading(false);
    }, 500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simulate API call
    setTimeout(() => {
      toast.success(t('client_updated_successfully'));
      navigate('/accountant-portal/clients');
    }, 500);
  };

  if (loading) {
    return (
      <AccountantLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress />
        </Box>
      </AccountantLayout>
    );
  }

  return (
    <AccountantLayout>
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/accountant-portal/clients')}
            sx={{ textTransform: 'none' }}
          >
            {t('back')}
          </Button>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {t('edit_client')}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('update_client_information')}
            </Typography>
          </Box>
        </Box>

        <Paper sx={{ p: 4, borderRadius: '12px' }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('client_name')}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label={t('email')}
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('phone')}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  required
                  label={t('industry')}
                  name="industry"
                  value={formData.industry}
                  onChange={handleChange}
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('address')}
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('vat_number')}
                  name="vatNumber"
                  value={formData.vatNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('billing_contact')}
                  name="billingContact"
                  value={formData.billingContact}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('notes')}
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/accountant-portal/clients')}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={{
                      textTransform: 'none',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, #5568d3 0%, #653a8b 100%)',
                      },
                    }}
                  >
                    {t('update_client')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Box>
    </AccountantLayout>
  );
};

export default EditClientPage;
